import { getRangerLevel, rangerMasteryCount } from '../../../shared/rules/ranger.mjs';
import { getPaladinLevel, paladinMasteryCount } from '../../../shared/rules/paladin.mjs';

export const normalizeWeaponKey = (value) => String(
  value && typeof value === 'object' ? value.type || value.key || value.name || '' : value || ''
).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const getClassLevel = (character, className) => (Array.isArray(character?.occupation)
  ? character.occupation.reduce((sum, entry) => {
      const name = String(entry?.class ?? entry?.Occupation ?? entry?.Name ?? entry?.occupation ?? entry?.name ?? '').trim().toLowerCase();
      return sum + (name === className.toLowerCase() ? Number(entry?.Level ?? entry?.level) || 0 : 0);
    }, 0) : 0);

const propertyBase = (value) => normalizeWeaponKey(value).split('-')[0];
export const isEligibleMasteryWeapon = (sourceId, weapon) => {
  const category = String(weapon?.category || '').toLowerCase();
  // Fighter, Ranger and Paladin are proficient with every Simple and Martial weapon.
  if (['fighter', 'ranger', 'paladin'].includes(sourceId)) return category.startsWith('simple ') || category.startsWith('martial ');
  if (sourceId === 'barbarian') return ['simple melee', 'martial melee'].includes(category);
  if (sourceId === 'rogue') return category.startsWith('simple ') ||
    (category.startsWith('martial ') && (weapon?.properties || []).some((p) => ['finesse', 'light'].includes(propertyBase(p))));
  return false;
};

export const getWeaponMasteryEntitlements = (character, catalog = {}) => {
  const result = [];
  const barbarian = getClassLevel(character, 'barbarian');
  const fighter = getClassLevel(character, 'fighter');
  const rogue = getClassLevel(character, 'rogue');
  const keys = (sourceId) => Object.entries(catalog).filter(([, weapon]) => isEligibleMasteryWeapon(sourceId, weapon)).map(([key]) => normalizeWeaponKey(key));
  if (fighter > 0) result.push({ sourceId: 'fighter', label: 'Fighter', count: fighter < 4 ? 3 : fighter < 10 ? 4 : fighter < 16 ? 5 : 6, eligibleWeaponKeys: keys('fighter') });
  if (barbarian) result.push({ sourceId: 'barbarian', label: 'Barbarian', count: barbarian < 4 ? 2 : barbarian < 10 ? 3 : 4, eligibleWeaponKeys: keys('barbarian') });
  if (rogue) result.push({ sourceId: 'rogue', label: 'Rogue', count: 2, eligibleWeaponKeys: keys('rogue') });
  const ranger = getRangerLevel(character);
  if (ranger) result.push({ sourceId: 'ranger', label: 'Ranger', count: rangerMasteryCount(ranger), eligibleWeaponKeys: keys('ranger') });
  const paladin = getPaladinLevel(character);
  if (paladin) result.push({ sourceId: 'paladin', label: 'Paladin', count: paladinMasteryCount(paladin), eligibleWeaponKeys: keys('paladin') });
  return result;
};

export const getSelectionsForSource = (character, sourceId) => {
  const selected = character?.weaponMasteries?.sources?.[sourceId]?.selections;
  if (Array.isArray(selected)) return selected.map(normalizeWeaponKey).filter(Boolean);
  if (sourceId === 'barbarian') return (character?.classState?.barbarian?.weaponMasteries?.selections || []).map(normalizeWeaponKey).filter(Boolean);
  return [];
};

export const hasWeaponMastery = (character, weapon) => {
  const key = normalizeWeaponKey(weapon);
  return getWeaponMasteryEntitlements(character).some(({ sourceId }) => getSelectionsForSource(character, sourceId).includes(key));
};

/** What each 2024 mastery property does (SRD 5.2.1), keyed by lowercase name. */
export const WEAPON_MASTERY_DESCRIPTIONS = Object.freeze({
  cleave: 'If you hit a creature with a melee attack, you can make a melee attack against a second creature within 5 feet of the first and within your reach. On a hit it takes the weapon’s damage, without your ability modifier unless that modifier is negative. Once per turn.',
  graze: 'If your attack misses a creature, you can deal damage to it equal to the ability modifier you used for the attack, of the weapon’s damage type.',
  nick: 'When you make the extra attack of the Light property, you can make it as part of the Attack action instead of as a Bonus Action. Once per turn.',
  push: 'If you hit a creature, you can push it up to 10 feet straight away from you if it is Large or smaller.',
  sap: 'If you hit a creature, it has Disadvantage on its next attack roll before the start of your next turn.',
  slow: 'If you hit a creature and deal damage to it, you can reduce its Speed by 10 feet until the start of your next turn. Hitting it again with a Slow weapon doesn’t reduce it further.',
  topple: 'If you hit a creature, you can force it to make a Constitution save (DC 8 + the ability modifier used for the attack + your Proficiency Bonus). On a failed save it has the Prone condition.',
  vex: 'If you hit a creature and deal damage to it, you have Advantage on your next attack roll against it before the end of your next turn.',
});
